import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ClampLines from '../ClampLines';

import IconCalendarDay from '../IconCalendarDay';
import Icon from '../Icon';
import Link from '../Link';
import Rga from '../Rga';
import pinSVG from '../../svg/icons/ic_location_20x20.svg';
import videoSVG from '../../svg/icons/video.svg';
import TranslationProvider from '../../providers/TranslationProvider';
import getImage from './getImage';
import APP_CONTEXT from '../../constants/appContext';
import containerQueryProps from '../../props/containerQueryProps';
import { getLocationSlug } from '../../utils';

import './EventCard.scss';

const getFormattedDateTime = ({
  date, time, endTime, eventType,
}) => {
  const endTimeFormatted = (endTime && eventType !== 1) ? ` - ${moment(endTime, 'HH:mm').format('LT')}` : '';
  const startToEnd = `${moment(`${date} ${time}`).format('llll')}${endTimeFormatted}`;

  if (!endTimeFormatted) {
    return <span>{startToEnd}</span>;
  }

  return (
    <ClampLines
      text={startToEnd}
      id={`event-card-${date}-${startToEnd}`}
      lines={2}
      buttons={false}
    />
  );
};

const renderLocation = (venue, isOnline) => {
  if (isOnline) {
    return (
      <div className="neighborhood">
        <Icon component={videoSVG} iconClass="video" />
        <TranslationProvider translationKey="virtual">
          <span>Virtual</span>
        </TranslationProvider>
      </div>
    );
  }

  return (
    <div className="neighborhood">
      <Icon component={pinSVG} iconClass="pin" />
      {venue.neighborhood}
    </div>
  );
};

const EventCard = (props) => {
  const {
    appContext,
    className,
    containerQuery: { md },
    context,
    date,
    end_time: endTime,
    event_type: eventType,
    image_preview: imagePreview,
    responsive_images: images,
    is_online: isOnline,
    is_pinned: isPinned,
    is_global_dining_access: isGdaEvent,
    isGda: isGdaEnabled,
    name,
    preview,
    time,
    url_slug: urlSlug,
    venue,
  } = props;

  let useLargerImage = false;

  if (!venue || !urlSlug) {
    return null;
  }

  if (isPinned && md) {
    useLargerImage = true;
  }

  const getTitle = () => {
    if (context === 'venue-page') {
      if (isGdaEvent) {
        return <Rga variant="bold" />;
      }

      return (
        <TranslationProvider translationKey="event">
          <div className="venue-name">Event</div>
        </TranslationProvider>
      );
    }

    return (
      <div className="venue-name">
        <ClampLines
          text={venue.name}
          id={`event-card-venue-${venue.urlSlug}-${urlSlug}`}
          lines={2}
          buttons={false}
          ellipsis="..."
        />
      </div>
    );
  };

  const getEventHref = () => {
    const prefix = appContext === APP_CONTEXT.WIDGET ? '#/' : '';
    const locationSlug = getLocationSlug(venue.location);
    const href = `${prefix}cities/${locationSlug}/venues/${venue.urlSlug}/events/${urlSlug}${window.location.search}`;
    return href;
  };

  const endTimeFormatted = (endTime && eventType !== 1) ? ` - ${moment(endTime, 'HH:mm').format('LT')}` : '';
  const getAriaLabel = () => `${name}, ${venue.name}, ${moment(`${date} ${time}`).format('LLLL')}${endTimeFormatted}`;

  return (
    <div
      className={cx('EventCard', {
        [className]: typeof className !== 'undefined',
        EventCardPinned: isPinned,
        EventCardRGA: (isGdaEnabled && isGdaEvent),
      })}
      data-test-id="upcoming-event-test-card"
    >
      <div className="calendar-icon"><IconCalendarDay date={date} /></div>
      <div className="image">
        <img src={getImage(images, imagePreview, useLargerImage)} alt="event" />
      </div>
      <div className="info">
        {getTitle()}

        <Link
          aria-label={getAriaLabel()}
          className="event-name"
          href={getEventHref()}
        >
          <ClampLines
            text={name}
            id={`event-card-name-${venue.urlSlug}-${urlSlug}`}
            lines={2}
            buttons={false}
            ellipsis="..."
          />
        </Link>
        <div className="date">
          {getFormattedDateTime({
            date, time, endTime, eventType,
          })}
        </div>
        {renderLocation(venue, isOnline)}
        {isPinned
          && (
          <div className="preview">
            {preview}
          </div>
          )}
      </div>
    </div>
  );
};

EventCard.propTypes = {
  appContext: PropTypes.oneOf(Object.values(APP_CONTEXT)).isRequired,
  className: PropTypes.string,
  context: PropTypes.oneOf(['venue-page', undefined]),
  containerQuery: containerQueryProps,
  name: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  preview: PropTypes.string.isRequired,
  image_preview: PropTypes.string,
  is_online: PropTypes.bool,
  is_pinned: PropTypes.bool,
  is_global_dining_access: PropTypes.bool,
  isGda: PropTypes.bool,
  time: PropTypes.string.isRequired,
  end_time: PropTypes.string,
  event_type: PropTypes.number.isRequired,
  venue: PropTypes.shape({
    locality: PropTypes.string,
    location: PropTypes.shape({}),
    name: PropTypes.string.isRequired,
    neighborhood: PropTypes.string.isRequired,
    urlSlug: PropTypes.string.isRequired,
  }).isRequired,
  responsive_images: PropTypes.shape({
    urls: PropTypes.shape(),
    file_names: PropTypes.arrayOf(PropTypes.string),
    aspect_ratios: PropTypes.shape(),
  }),
  url_slug: PropTypes.string.isRequired,
};

EventCard.defaultProps = {
  className: undefined,
  context: undefined,
  containerQuery: {},
  end_time: null,
  image_preview: undefined,
  is_online: false,
  is_pinned: false,
  is_global_dining_access: false,
  isGda: false,
  responsive_images: undefined,
};

EventCard.displayName = 'EventCard';
export default EventCard;
